import {
	useLocation,
	useNavigate,
	useOutletContext,
	useParams,
} from 'react-router-dom';
import { Colors, Text, H3, TextSize } from '@quizzach/styles';
import { QuizSlidesContainer, NavButtonWrapper } from '@quizzach/play-styles';
import { QuizOutletContextType, RoutePath } from '@quizzach/interfaces';
import { markHandwrittenWordsInText, scrollToTop } from '@quizzach/utils';
import { useEffect, useState } from 'react';
import {
	UserOwnedPremadeQuizzesProvider,
	useWindowListener,
} from '@quizzach/contexts';
import {
	BottomNav,
	FitToViewport,
	PremadeFeedback,
} from '@quizzach/components';
import { BottomNavMobile } from '@quizzach/layouts';
import { popInMotionConfig } from '@quizzach/motion';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface QuizBreakContentProps {
	title: string;
	message?: string;
	handLabel?: string;
	onButtonClick?: () => void;
	feedback: boolean;
	premadeFeedback?: boolean;
	touchDevice: boolean;
}

const QuizBreakContent: React.FC<QuizBreakContentProps> = ({
	title,
	message,
	handLabel,
	onButtonClick,
	feedback,
	premadeFeedback,
	touchDevice,
}) => {
	const { t } = useTranslation();
	return (
		<>
			<motion.div className='quiz-break-content' {...popInMotionConfig()}>
				<div className='quiz-break-main'>
					{handLabel && <span className='break-hand-label'>{handLabel}</span>}
					<H3 color={Colors.textMain}>{title}</H3>
					{message && (
						<Text
							as='div'
							$size={touchDevice ? TextSize.m : TextSize.l}
							color={Colors.textSoft}
							dangerouslySetInnerHTML={{
								__html: markHandwrittenWordsInText(message),
							}}
						/>
					)}
				</div>
				{feedback && (
					<div className='quiz-break-feedback'>
						<span className='break-hand-label'>
							{t('quiz.break.feedbackTitle')}
						</span>
						<Text
							$size={touchDevice ? TextSize.s : TextSize.m}
							color={Colors.textSoft}
						>
							{t('quiz.break.feedbackMessage')}
						</Text>
					</div>
				)}
			</motion.div>
			{premadeFeedback && (
				<UserOwnedPremadeQuizzesProvider>
					<PremadeFeedback
						onButtonClick={onButtonClick ?? (() => {})}
						buttonText={t('quiz.break.finish')}
					/>
				</UserOwnedPremadeQuizzesProvider>
			)}
		</>
	);
};

export const QuizBreak = () => {
	const location = useLocation();
	// const quizType = location.state?.quizType;
	const currentPath = location.pathname;
	const { uuid } = useParams<{ uuid: string }>();
	const { touchDevice } = useWindowListener();

	const { t } = useTranslation();
	const { quizType, quiz, currentQuestionNumber, handlePrevious, handleNext } =
		useOutletContext<QuizOutletContextType>();
	const route = currentPath.split(`/quiz/${uuid}/`)[1];
	const navigate = useNavigate();
	const [isFinishHovered, setIsFinishHovered] = useState(false);

	useEffect(() => {
		scrollToTop();
	}, []);

	const renderContent = () => {
		switch (route) {
			case RoutePath.QuizMidBreak1:
				return (
					<QuizBreakContent
						title={t('quiz.break.answersTitle')}
						handLabel={t('quiz.break.firstHalfHand')}
						feedback={quizType !== 'premade'}
						touchDevice={touchDevice}
					/>
				);
			case RoutePath.QuizMidBreak2:
				return (
					<QuizBreakContent
						title={t('quiz.break.answersTitle')}
						handLabel={t('quiz.break.secondHalfHand')}
						feedback={quizType !== 'premade'}
						touchDevice={touchDevice}
					/>
				);
			case RoutePath.QuizMidBreakSingle:
				return (
					<QuizBreakContent
						title={t('quiz.break.answersTitle')}
						handLabel={t('quiz.break.allQuestionsHand')}
						feedback={quizType !== 'premade'}
						touchDevice={touchDevice}
					/>
				);
			case RoutePath.QuizHalftime:
				return (
					<QuizBreakContent
						title={t('quiz.break.halftimeTitle')}
						handLabel={t('quiz.break.halftimeHand')}
						feedback={false}
						touchDevice={touchDevice}
					/>
				);
			case RoutePath.QuizEstimationIntro:
				return (
					<QuizBreakContent
						title={t('quiz.break.estimationTitle')}
						message={t('quiz.break.estimationMessage')}
						feedback={false}
						touchDevice={touchDevice}
					/>
				);
			case RoutePath.QuizEnd:
				return (
					<QuizBreakContent
						title={t('quiz.break.endTitle')}
						handLabel={t('quiz.break.endHand')}
						onButtonClick={handleNext}
						feedback={false}
						premadeFeedback={quizType === 'premade'}
						touchDevice={touchDevice}
					/>
				);
			default:
				return (
					<>
						<Text $size={TextSize.s} color={Colors.textSoft}>
							{t('quiz.break.errorRetry')}
						</Text>
					</>
				);
		}
	};

	return (
		<QuizSlidesContainer
			className='slides-centered'
			data-id='quiz-slides-container-03'
		>
			{/* Mobile scrolls naturally; the fit-to-viewport scaling is a
			    desktop/presentation concern. */}
			{touchDevice ? (
				renderContent()
			) : (
				<FitToViewport>{renderContent()}</FitToViewport>
			)}
			{route !== RoutePath.QuizEnd &&
				(touchDevice ? (
					<BottomNavMobile
						question={quiz?.questions[currentQuestionNumber - 1]}
						handleNext={handleNext}
						handlePrevious={handlePrevious}
						isPremadeOrEstimation={true}
						type='plain'
					/>
				) : (
					<BottomNav
						question={quiz?.questions[currentQuestionNumber - 1]}
						handleNext={handleNext}
						handlePrevious={handlePrevious}
						isPremadeOrEstimation={true}
						type='plain'
					/>
				))}
			{quizType !== 'premade' && route === RoutePath.QuizEnd && (
				<NavButtonWrapper
					onClick={() => {
						if (navigator.vibrate) navigator.vibrate(30);
						navigate(RoutePath.Profile);
					}}
					onMouseEnter={() => setIsFinishHovered(true)}
					onMouseLeave={() => setIsFinishHovered(false)}
				>
					<Text
						$size={TextSize.l}
						color={isFinishHovered ? Colors.textReverse : Colors.textBrand}
						style={{ margin: 0 }}
					>
						{t('quiz.break.finish')}
					</Text>
				</NavButtonWrapper>
			)}
		</QuizSlidesContainer>
	);
};
